import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Award, Users, Calendar, MessageSquare, Target, TrendingUp, Code, BookOpen, AlertTriangle, Clock, Lightbulb } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useAuth } from "@/contexts/AuthContext";
import { calculateJoinedDaysAgo } from "@/utils/calculateJoinedDays";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { triggerMilestoneCheck } from "@/services/onboardingMilestoneService";
import { useNavigate } from "react-router-dom";
import { connectCalendarProvider } from "@/services/calendarService";
import { calendarService } from "@/services/calendarService";
import { wellnessService } from "@/services/wellnessService";
import { goalsService } from "@/services/goalsService";
import { skillsService } from "@/services/skillsService";

interface OnboardingTask {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  icon: React.ReactNode;
  action?: () => void;
  milestoneType?: string;
  category: 'essentials' | 'productivity' | 'growth';
}

interface IndividualContributorOnboardingProps {
  setActiveTab?: (tab: string) => void;
}

export default function IndividualContributorOnboarding({ setActiveTab }: IndividualContributorOnboardingProps) {
  const { user, updateUserProfile } = useUser();
  const { isAuthenticated } = useAuth();
  const [daysAgo, setDaysAgo] = useState(0);
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [hasCheckIns, setHasCheckIns] = useState(false);
  const [checkInsCount, setCheckInsCount] = useState(0);
  const [hasSkills, setHasSkills] = useState(false);
  const [hasGoals, setHasGoals] = useState(false);
  const [hasProductivityPattern, setHasProductivityPattern] = useState(false);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [insightsData, setInsightsData] = useState<any>(null);
  const [goalsData, setGoalsData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.joinedDate) {
      setDaysAgo(calculateJoinedDaysAgo(user.joinedDate));
    }
  }, [user?.joinedDate]);

  // Check real completion status for each task
  useEffect(() => {
    const checkTaskCompletion = async () => {
      if (!user?.id) return;

      try {
        // Parallel fetch all data from API
        // Note: apiService.get returns data directly, not { success, data }
        const [calendarResponse, statsResponse, goalsResponse, skillsResponse] = await Promise.all([
          // Check calendar integration
          calendarService.getIntegrations().catch(() => []),

          // Get check-ins count from stats
          wellnessService.getStats().catch(() => ({ totalCheckIns: 0 })),

          // Check if user has goals
          goalsService.getGoals().catch(() => []),

          // Check if user has skills (mentor_skills table)
          skillsService.getMySkills().catch(() => [])
        ]);

        // Set calendar status - response is array directly
        const calendarData = Array.isArray(calendarResponse) 
          ? calendarResponse 
          : (calendarResponse?.integrations || []);
        setHasCalendar(calendarData.length > 0);

        // Set check-ins status - response is object directly
        const totalCheckIns = statsResponse?.totalCheckIns || 0;
        setHasCheckIns(totalCheckIns > 0);
        setCheckInsCount(totalCheckIns);

        // Check if user has set skills - from API response
        const skills = Array.isArray(skillsResponse) 
          ? skillsResponse 
          : (skillsResponse?.skills || []);
        setHasSkills(skills.length > 0);

        // Set goals status - response is array directly or object with goals property
        const goals = Array.isArray(goalsResponse) 
          ? goalsResponse 
          : (goalsResponse?.goals || []);
        setHasGoals(goals.length > 0);

        // Check productivity pattern (after 5 check-ins)
        setHasProductivityPattern(totalCheckIns >= 5);

      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error checking task completion:', error);
        }
      }
    };

    checkTaskCompletion();
  }, [user, checkInsCount]);

  // Fetch insights data when user has 5+ check-ins
  useEffect(() => {
    const fetchInsights = async () => {
      if (!user?.id || checkInsCount < 5) return;

      console.log('Fetching insights for user:', user.id, 'with', checkInsCount, 'check-ins');

      // Set a safety timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.log('Insights fetch timeout, setting fallback data');
        setInsightsData({
          avgEnergy: 65,
          avgFocus: 65,
          avgMotivation: 65,
          peakDay: 'Thursday',
          checkInsCount,
          streak: checkInsCount >= 7 ? Math.floor(checkInsCount / 7) : 0
        });
      }, 15000); // 15 second timeout

      try {
        // Get recent check-ins for insights
        const checkInsResponse = await wellnessService.getCheckIns({ limit: 14 });
        const recentCheckIns = checkInsResponse.data || checkInsResponse || [];

        console.log('Fetched check-ins:', recentCheckIns?.length || 0);

        if (recentCheckIns && recentCheckIns.length > 0) {
          // Calculate insights
          const avgEnergyRaw = recentCheckIns.reduce((sum, c) => sum + (c.energy || 0), 0) / recentCheckIns.length;
          const avgFocusRaw = recentCheckIns.reduce((sum, c) => sum + (c.focus || 0), 0) / recentCheckIns.length;

          const motivationValues = recentCheckIns
            .map((c) => (typeof c.motivation === 'number' ? c.motivation : null))
            .filter((value): value is number => value !== null);
          const avgMotivationRaw = motivationValues.length
            ? motivationValues.reduce((sum, value) => sum + value, 0) / motivationValues.length
            : (avgEnergyRaw + avgFocusRaw) / 2;
          
          // Find peak day
          const dayStats: Record<string, { total: number; count: number }> = {};
          recentCheckIns.forEach((checkin: any) => {
            const day = new Date(checkin.createdAt || checkin.created_at).toLocaleDateString('en-US', { weekday: 'long' });
            if (!dayStats[day]) {
              dayStats[day] = { total: 0, count: 0 };
            }
            dayStats[day].total += (checkin.energy || 0) + (checkin.focus || 0);
            dayStats[day].count += 1;
          });

          const peakDay = Object.entries(dayStats).reduce((peak, [day, stats]) => {
            const avg = stats.total / stats.count;
            return avg > peak.avg ? { day, avg } : peak;
          }, { day: 'Monday', avg: 0 });

          const insights = {
            avgEnergy: Math.round(avgEnergyRaw),
            avgFocus: Math.round(avgFocusRaw),
            avgMotivation: Math.round(avgMotivationRaw),
            peakDay: peakDay.day,
            checkInsCount,
            streak: checkInsCount >= 7 ? Math.floor(checkInsCount / 7) : 0
          };

          console.log('Setting insights data:', insights);
          setInsightsData(insights);
          clearTimeout(timeoutId); // Clear timeout on success
        } else {
          // No check-ins found, set fallback data
          console.log('No check-ins found, setting fallback data');
          setInsightsData({
            avgEnergy: 50,
            avgFocus: 50,
            avgMotivation: 50,
            peakDay: 'Monday',
            checkInsCount,
            streak: 0
          });
          clearTimeout(timeoutId); // Clear timeout on completion
        }
      } catch (error) {
        console.error('Error fetching insights:', error);
        clearTimeout(timeoutId); // Clear timeout on error
        // Set fallback data to prevent infinite loading
        setInsightsData({
          avgEnergy: 60,
          avgFocus: 60,
          avgMotivation: 60,
          peakDay: 'Tuesday',
          checkInsCount,
          streak: checkInsCount >= 7 ? Math.floor(checkInsCount / 7) : 0
        });
      }
    };

    fetchInsights();
  }, [user?.id, checkInsCount]);

  // Fetch goals data for enhanced insights
  useEffect(() => {
    const fetchGoalsData = async () => {
      if (!user?.id || checkInsCount < 5) return;

      try {
        const goalsResponse = await goalsService.getGoals();
        const goals = goalsResponse.data || goalsResponse || [];

        // Calculate goal insights
        const activeGoals = goals?.filter((g: any) => g.status !== 'completed') || [];
        const completedGoals = goals?.filter((g: any) => g.status === 'completed') || [];
        const overDueGoals = activeGoals.filter((g: any) => g.targetDate && new Date(g.targetDate) < new Date());
        const nearDueGoals = activeGoals.filter((g: any) => {
          if (!g.targetDate) return false;
          const daysUntilDue = Math.ceil((new Date(g.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
          return daysUntilDue <= 7 && daysUntilDue > 0;
        });

        const avgProgress = activeGoals.length > 0 
          ? activeGoals.reduce((sum: number, g: any) => sum + (g.progress || 0), 0) / activeGoals.length 
          : 0;

        setGoalsData({
          total: goals?.length || 0,
          active: activeGoals.length,
          completed: completedGoals.length,
          overDue: overDueGoals.length,
          nearDue: nearDueGoals.length,
          avgProgress: Math.round(avgProgress),
          recentGoals: activeGoals.slice(0, 2), // Show top 2 active goals
          completedThisWeek: completedGoals.filter((g: any) => {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return new Date(g.createdAt || g.created_at) > weekAgo;
          }).length
        });
      } catch (error) {
        console.error('Error fetching goals data:', error);
      }
    };

    fetchGoalsData();
  }, [user?.id, checkInsCount]);

  // Handle task completion
  const handleTaskCompletion = useCallback(async (taskType: string) => {
    if (!user?.id) return;

    try {
      // Trigger milestone check - use specific milestone types
      const milestoneType = taskType === 'calendar' ? 'calendar_connect' : 
                          taskType === 'checkin' ? 'checkin_complete' :
                          taskType === 'profile' ? 'profile_update' : 'checkin_complete';
      await triggerMilestoneCheck(user.id, milestoneType);
      
      // Show success toast
      toast.success('Task completed! 🎉');
    } catch (error) {
      console.error('Error completing task:', error);
    }
  }, [user]);

  useEffect(() => {
    // Define tasks with real completion status and working actions
    const allTasks: OnboardingTask[] = [
      // Essential Tasks
      {
        id: "profile",
        title: "Complete your profile",
        description: "Add your bio and technical skills",
        completed: !!(user?.bio && hasSkills),
        icon: <Users className="h-4 w-4" />,
        category: 'essentials',
        milestoneType: 'profile_completed',
        action: async () => {
          navigate('/profile');
          if (user?.bio && hasSkills) {
            await handleTaskCompletion('profile_update');
          }
        }
      },
      {
        id: "first-checkin",
        title: "Complete your first check-in",
        description: "Start tracking your wellbeing",
        completed: hasCheckIns,
        icon: <CheckCircle2 className="h-4 w-4" />,
        category: 'essentials',
        milestoneType: 'first_checkin',
        action: async () => {
          navigate('/check-in');
        }
      },
      {
        id: "calendar",
        title: "Connect your calendar",
        description: "Track meetings and focus time",
        completed: hasCalendar,
        icon: <Calendar className="h-4 w-4" />,
        category: 'essentials',
        milestoneType: 'calendar_connected',
        action: async () => {
          if (!isAuthenticated || !user?.id) {
            toast.error("Please sign in to connect your calendar.");
            return;
          }

          // Check if calendar is already connected
          if (hasCalendar) {
            toast.info("Your calendar is already connected! ✅");
            await handleTaskCompletion('calendar_connected');
            return;
          }

          setIsConnectingCalendar(true);
          try {
            const success = await connectCalendarProvider('google', '', user.id);
            setIsConnectingCalendar(false);
            
            if (success) {
              setHasCalendar(true);
              toast.success("Calendar is already connected! ✅");
              await handleTaskCompletion('calendar_connected');
            } else {
              toast.error("Failed to connect calendar. Please try again.");
            }
          } catch (error) {
            console.error("Calendar connection error:", error);
            setIsConnectingCalendar(false);
            const errorMessage = error instanceof Error ? error.message : String(error);
            
            // Handle duplicate connection error specifically
            if (errorMessage.includes('already exists') || errorMessage.includes('23505')) {
              setHasCalendar(true);
              toast.success("Calendar is already connected! ✅");
              await handleTaskCompletion('calendar_connected');
            } else {
              toast.error("Failed to connect calendar. Please try again.");
            }
          }
        }
      },
      
      // Productivity Tasks
      {
        id: "skills-tracking",
        title: "Set up skill tracking",
        description: "Track technologies you're using",
        completed: hasSkills,
        icon: <Code className="h-4 w-4" />,
        category: 'productivity',
        action: async () => {
          navigate('/profile');
          toast.info('Add skills you want to track in your profile.');
        }
      },
      {
        id: "productivity-pattern",
        title: "Establish productivity baseline",
        description: `Complete 5 check-ins (${checkInsCount}/5)`,
        completed: hasProductivityPattern,
        icon: <TrendingUp className="h-4 w-4" />,
        category: 'productivity',
        action: async () => {
          if (!hasProductivityPattern) {
            navigate('/check-in');
            toast.info(`You need ${5 - checkInsCount} more check-ins to establish your baseline.`);
          }
        }
      },
      
      // Growth Tasks
      {
        id: "set-goals",
        title: "Set your first goal",
        description: "Define what you want to achieve",
        completed: hasGoals,
        icon: <Target className="h-4 w-4" />,
        category: 'growth',
        action: async () => {
          navigate('/profile');
          toast.info('Go to Profile → Goals tab → Click "Add Goal" to create your first development goal', {
            duration: 6000,
          });
        }
      },
      {
        id: "explore-insights",
        title: "Review your insights",
        description: "See patterns in your work",
        completed: checkInsCount >= 3,
        icon: <BookOpen className="h-4 w-4" />,
        category: 'growth',
        action: async () => {
          if (setActiveTab) {
            setActiveTab('insights');
          } else {
            navigate('/');
            toast.info('Navigate to the Insights tab on your dashboard.');
          }
        }
      }
    ];

    setTasks(allTasks);
  }, [user, hasCalendar, hasCheckIns, hasSkills, hasGoals, hasProductivityPattern, checkInsCount, setActiveTab, navigate, handleTaskCompletion, isAuthenticated, isConnectingCalendar]);

  const completedTasks = tasks.filter(t => t.completed).length;
  const progress = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;

  // Get tasks by category
  const essentialTasks = tasks.filter(t => t.category === 'essentials');
  const productivityTasks = tasks.filter(t => t.category === 'productivity');
  const growthTasks = tasks.filter(t => t.category === 'growth');

  // Graduated messaging based on days and progress
  const getWelcomeMessage = () => {
    if (progress === 100) return "🎉 You're all set up! Time to focus on your work.";
    if (daysAgo < 7) return "Welcome to Step! Let's set up your productivity toolkit.";
    if (daysAgo < 30) return "Great start! Complete these tasks to unlock insights.";
    if (daysAgo < 60) return "You're making progress! Finish setup to maximize Step's value.";
    return "Almost there! Complete these final tasks.";
  };

  const renderTaskSection = (title: string, tasks: OnboardingTask[], icon: React.ReactNode) => {
    const completedCount = tasks.filter(t => t.completed).length;
    const allCompleted = completedCount === tasks.length;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          {icon}
          <span>{title}</span>
          {allCompleted && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          <Badge variant="secondary" className="ml-auto">{completedCount}/{tasks.length}</Badge>
        </div>
        <div className="space-y-2">
          {tasks.map((task) => (
            <div
              key={task.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                task.completed 
                  ? 'bg-muted/30 border-muted' 
                  : 'bg-background hover:bg-muted/10 transition-colors'
              }`}
            >
              <div className="flex items-center gap-3">
                {task.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <div className="font-medium text-sm">{task.title}</div>
                  <div className="text-xs text-muted-foreground">{task.description}</div>
                </div>
              </div>
              {!task.completed && task.action && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  disabled={task.id === 'calendar' && isConnectingCalendar}
                  onClick={(e) => {
                    e.stopPropagation();
                    task.action();
                  }}
                >
                  {task.id === 'calendar' && isConnectingCalendar 
                    ? 'Connecting...' 
                    : task.id === 'productivity-pattern' && checkInsCount > 0 
                    ? 'Continue' 
                    : 'Start'}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Transition to Personal Insights after 5 check-ins
  if (checkInsCount >= 5) {
    // Show loading state while insights data is being fetched
    if (!insightsData) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              Personal Insights
            </CardTitle>
            <CardDescription>Loading your productivity patterns...</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Personal Insights
              </CardTitle>
              <CardDescription>
                Your productivity patterns from {checkInsCount} check-ins
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {insightsData.streak > 0 ? `${insightsData.streak} week streak` : 'Building habits'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Top Row - All Key Metrics in Single Row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-950/30 rounded border">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{insightsData.avgEnergy}%</div>
              <div className="text-xs text-muted-foreground">Energy</div>
            </div>
            <div className="text-center p-2 bg-green-50 dark:bg-green-950/30 rounded border">
              <div className="text-xl font-bold text-green-600 dark:text-green-400">{insightsData.avgFocus}%</div>
              <div className="text-xs text-muted-foreground">Focus</div>
            </div>
            <div className="text-center p-2 bg-purple-50 dark:bg-purple-950/30 rounded border">
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{insightsData.avgMotivation}%</div>
              <div className="text-xs text-muted-foreground">Motivation</div>
            </div>
            <div className="text-center p-2 bg-amber-50 dark:bg-amber-950/30 rounded border">
              <div className="text-sm font-bold text-amber-600 dark:text-amber-400">{insightsData.peakDay}</div>
              <div className="text-xs text-muted-foreground">Peak Day</div>
            </div>
          </div>

          {/* Goals & Progress - Horizontal Layout */}
          {goalsData && goalsData.total > 0 && (
            <div className="border rounded-lg p-3 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-sm">Goals Progress</span>
                  <Badge variant="outline" className="text-xs">{goalsData.active} active</Badge>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-emerald-600 font-medium">{goalsData.avgProgress}% avg</span>
                  <span className="text-blue-600 font-medium">{goalsData.completed} done</span>
                </div>
              </div>
              
              {/* Current Goal */}
              {goalsData.recentGoals && goalsData.recentGoals.length > 0 && (
                <div className="p-2 bg-white/60 dark:bg-gray-800/60 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate flex-1">{goalsData.recentGoals[0].title}</span>
                    <span className="text-xs text-muted-foreground ml-2">{goalsData.recentGoals[0].progress || 0}%</span>
                  </div>
                  <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300" 
                      style={{ width: `${goalsData.recentGoals[0].progress || 0}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Smart Insights & Progress Summary - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Smart Insights */}
            <div className="border rounded-lg p-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-sm">Smart Insights</span>
              </div>
              <div className="space-y-1">
                {goalsData?.overDue > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <AlertTriangle className="h-3 w-3 text-red-500" />
                    <span className="text-red-700 dark:text-red-300">{goalsData.overDue} overdue</span>
                  </div>
                )}
                {goalsData?.nearDue > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 text-orange-500" />
                    <span className="text-orange-700 dark:text-orange-300">{goalsData.nearDue} due soon</span>
                  </div>
                )}
                {insightsData.avgEnergy < 50 ? (
                  <div className="flex items-center gap-2 text-xs">
                    <TrendingUp className="h-3 w-3 text-blue-500" />
                    <span className="text-blue-700 dark:text-blue-300">Consider more breaks</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="h-3 w-3 text-green-500" />
                    <span className="text-green-700 dark:text-green-300">Peak on {insightsData.peakDay}s</span>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Summary */}
            <div className="border rounded-lg p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-green-600" />
                <span className="font-medium text-sm">Progress Summary</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Check-in streak:</span>
                  <span className="font-medium">{checkInsCount} days</span>
                </div>
                {goalsData?.completedThisWeek > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Goals this week:</span>
                    <span className="font-medium text-green-600">{goalsData.completedThisWeek}</span>
                  </div>
                )}
                <div className="text-center pt-1">
                  <span className="text-xs font-medium text-green-700 dark:text-green-300">
                    🎉 {checkInsCount >= 10 ? 'Amazing consistency!' : 'Great progress!'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Inline & Compact */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex gap-2">
              <Button
                variant="outline" 
                size="sm"
                onClick={() => navigate('/patterns')}
                className="h-7 px-2 text-xs"
              >
                <BookOpen className="h-3 w-3 mr-1" />
                Insights
              </Button>
              <Button
                variant="outline"
                size="sm" 
                onClick={() => navigate('/check-in')}
                className="h-7 px-2 text-xs"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Check-in
              </Button>
              {goalsData && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/profile', { state: { activeTab: 'goals' } })}
                  className="h-7 px-2 text-xs"
                >
                  <Target className="h-3 w-3 mr-1" />
                  Goals
                </Button>
              )}
            </div>
            <Badge variant="secondary" className="text-xs">
              Week {insightsData.streak > 0 ? insightsData.streak : Math.ceil(checkInsCount / 7)}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-ic-onboarding>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Individual Contributor Setup
              {progress === 100 && <Award className="h-5 w-5 text-yellow-500" />}
            </CardTitle>
            <CardDescription>{getWelcomeMessage()}</CardDescription>
          </div>
          {progress < 100 && (
            <div className="flex flex-col items-end gap-1">
              {daysAgo >= 7 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      await updateUserProfile({ persona: 'individual' });
                      toast.success('Onboarding completed! Welcome to Step.');
                    }}
                  >
                    Skip Setup
                  </Button>
                  <span className="text-xs text-muted-foreground">Skip remaining tasks</span>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Skip available in {7 - daysAgo} days
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">{completedTasks} of {tasks.length} completed</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Essential Tasks */}
        {renderTaskSection('Essential Setup', essentialTasks, <CheckCircle2 className="h-4 w-4 text-blue-500" />)}
        
        {/* Productivity Tasks */}
        {renderTaskSection('Productivity Tools', productivityTasks, <TrendingUp className="h-4 w-4 text-purple-500" />)}
        
        {/* Growth Tasks */}
        {renderTaskSection('Growth & Development', growthTasks, <Target className="h-4 w-4 text-amber-500" />)}

        {progress === 100 && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-green-600" />
              <span className="font-medium">Onboarding Complete! 🎉</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Great job! You've completed all setup tasks. Continue using Step daily to unlock deeper insights about your productivity patterns.
            </p>
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <TrendingUp className="h-4 w-4" />
              <span>This card will evolve into Personal Insights once you complete 5 check-ins</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}